import { Loader2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { Button } from '@/components/ui/button'

import type { BobAnalyse, BobSuggestion } from '@/lib/services/bobAnalyseService'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { EditableBox } from './EditableBox'
import { AddSectionDialog } from './AddSectionDialog'
import { AddSourcesPanel } from './AddSourcesPanel'
import { CategorySuggestions } from './CategorySuggestions'
import { SourceHistoryPanel } from './SourceHistoryPanel'
import { websocketService } from '@/lib/services/websocketService'
import { useToast } from '@/lib/hooks/useToast'

interface BobAnalyseCardProps {
  bobAnalyses: BobAnalyse[]
  onRefetch: () => void
  selectedBobId: string
  onExportReady?: (exportFn: () => Promise<void>) => void
}

export function BobAnalyseCard({
  bobAnalyses,
  onRefetch,
  selectedBobId,
  onExportReady,
}: BobAnalyseCardProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const selectedBobAnalyse = bobAnalyses.find(bob => bob.id === selectedBobId)
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentBobAnalyseId, setCurrentBobAnalyseId] = useState<string | undefined>(
    selectedBobAnalyse?.id
  )
  const [forceRemount, setForceRemount] = useState<number>(0)
  const beeldvormingSaveRefs = useRef<Record<string, () => Promise<void>>>({})
  const oordeelsvormingSaveRefs = useRef<Record<string, () => Promise<void>>>({})
  const besluitvormingSaveRefs = useRef<Record<string, () => Promise<void>>>({})
  const beeldvormingGetValuesRefs = useRef<Record<string, () => string[]>>({})
  const oordeelsvormingGetValuesRefs = useRef<Record<string, () => string[]>>({})
  const besluitvormingGetValuesRefs = useRef<Record<string, () => string[]>>({})

  // Dialog state for adding new sections
  const [addSectionDialogOpen, setAddSectionDialogOpen] = useState(false)
  const [addSectionTarget, setAddSectionTarget] = useState<
    'beeldvorming' | 'oordeelsvorming' | 'besluitvorming' | null
  >(null)

  // Permission and crisis checks
  const { activeCrisis } = useCrisisStore()
  const canUpdateBob = useHasPermission('bob.update')
  const isClosedCrisis = activeCrisis?.status === 'closed'

  // ============================================================================
  // WebSocket Connection Management
  // ============================================================================

  useEffect(() => {
    if (!selectedBobId) return

    let mounted = true

    const initWebSocket = async () => {
      try {
        await websocketService.connect()
        if (mounted) {
          websocketService.joinBobAnalysis(selectedBobId)
        }
      } catch (error) {
        console.error('[BobAnalyseCard] WebSocket connection failed:', error)
      }
    }

    initWebSocket()

    return () => {
      mounted = false
      websocketService.leaveBobAnalysis()
    }
  }, [selectedBobId])

  // Subscribe to content updates - when a category is added/deleted, refetch
  useEffect(() => {
    let refetchTimeout: NodeJS.Timeout | null = null

    const unsubscribe = websocketService.onContentUpdate(() => {
      // Debounce refetch to avoid multiple rapid fetches
      if (refetchTimeout) {
        clearTimeout(refetchTimeout)
      }
      refetchTimeout = setTimeout(() => {
        console.log('[BobAnalyseCard] WebSocket triggered refetch')
        onRefetch()
      }, 300)
    })

    return () => {
      unsubscribe()
      if (refetchTimeout) {
        clearTimeout(refetchTimeout)
      }
    }
  }, [onRefetch])

  useEffect(() => {
    if (selectedBobAnalyse && currentBobAnalyseId !== selectedBobAnalyse.id) {
      setCurrentBobAnalyseId(selectedBobAnalyse.id)
      // Clear any in-progress updates

      // Force a remount of all EditableBox components
      setForceRemount(prev => prev + 1)
    }
  }, [selectedBobAnalyse, currentBobAnalyseId])

  // Create memoized export function
  const handleExport = useCallback(async () => {
    if (!selectedBobAnalyse) return
    const { exportBobAnalyseToWord } = await import('@/lib/utils')
    await exportBobAnalyseToWord(selectedBobAnalyse)
  }, [selectedBobAnalyse])

  // Expose export function to parent component
  useEffect(() => {
    if (onExportReady) {
      onExportReady(handleExport)
    }
  }, [handleExport, onExportReady])

  // Register an EditableBox save function with our refs
  const registerBoxSave = (
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    category: string,
    saveFunction: () => Promise<void>
  ) => {
    if (section === 'beeldvorming') {
      beeldvormingSaveRefs.current[category] = saveFunction
    } else if (section === 'oordeelsvorming') {
      oordeelsvormingSaveRefs.current[category] = saveFunction
    } else {
      besluitvormingSaveRefs.current[category] = saveFunction
    }
  }

  // Register an EditableBox getValues function with our refs
  const registerBoxGetValues = (
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    category: string,
    getValuesFunction: () => string[]
  ) => {
    console.log('[BobAnalyseCard] registerBoxGetValues called', { section, category })
    if (section === 'beeldvorming') {
      beeldvormingGetValuesRefs.current[category] = getValuesFunction
    } else if (section === 'oordeelsvorming') {
      oordeelsvormingGetValuesRefs.current[category] = getValuesFunction
    } else {
      besluitvormingGetValuesRefs.current[category] = getValuesFunction
    }
    console.log('[BobAnalyseCard] registerBoxGetValues completed', {
      section,
      category,
      registeredCategories: Object.keys(
        section === 'beeldvorming'
          ? beeldvormingGetValuesRefs.current
          : section === 'oordeelsvorming'
          ? oordeelsvormingGetValuesRefs.current
          : besluitvormingGetValuesRefs.current
      )
    })
  }

  // Update BOB content mutation
  // IMPORTANT: No onRefetch() call here - WebSocket handles real-time sync
  // Database save is fire-and-forget, WebSocket already broadcast the change
  const updateContentMutation = useMutation({
    mutationFn: ({
      section,
      category,
      values,
    }: {
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      category: string
      values: string[]
    }) => {
      if (!selectedBobAnalyse) throw new Error('No BOB analyse selected')

      return bobAnalyseService.updateBobAnalyseContent(
        selectedBobAnalyse.id,
        section,
        category,
        values
      )
    },
    // No onSuccess with onRefetch() - WebSocket is the source of truth
    onError: () => {
      console.error('Error updating BOB analysis')
    },
  })

  // Helper function to save BOB content - wrapped in useCallback for stability
  const handleSaveContent = useCallback(
    async (
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
      category: string,
      newValues: string[]
    ): Promise<void> => {
      // Check permissions before saving
      if (!canUpdateBob || isClosedCrisis) {
        console.debug('[BobAnalyseCard] Save blocked: no permission or crisis closed')
        return
      }

      console.debug('[BobAnalyseCard] Saving content:', { section, category, values: newValues })

      try {
        await updateContentMutation.mutateAsync({
          section,
          category,
          values: newValues,
        })
        console.debug('[BobAnalyseCard] Save successful')
      } catch (error) {
        console.error('[BobAnalyseCard] Save failed:', error)
        throw error // Re-throw so EditableBox knows it failed
      }
    },
    [canUpdateBob, isClosedCrisis, updateContentMutation]
  )

  // Add category mutation
  const addCategoryMutation = useMutation({
    mutationFn: ({
      section,
      categoryName,
    }: {
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      categoryName: string
    }) => {
      if (!selectedBobAnalyse) throw new Error('No BOB analyse selected')
      return bobAnalyseService.addCategory(selectedBobAnalyse.id, section, categoryName)
    },
    onSuccess: (_, variables) => {
      // Broadcast category addition via WebSocket
      websocketService.broadcastCategoryAdded(variables.section, variables.categoryName)

      toast({
        title: 'Sectie toegevoegd',
        description: 'De nieuwe sectie is succesvol toegevoegd.',
      })
      onRefetch()
    },
    onError: error => {
      console.error('Error adding category:', error)
      toast({
        title: 'Fout bij toevoegen',
        description: 'Er is een fout opgetreden bij het toevoegen van de sectie.',
        variant: 'destructive',
      })
    },
  })

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: ({
      section,
      categoryName,
      currentContent,
    }: {
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      categoryName: string
      currentContent: Record<string, string[]>
    }) => {
      if (!selectedBobAnalyse) throw new Error('No BOB analyse selected')
      return bobAnalyseService.deleteCategory(
        selectedBobAnalyse.id,
        section,
        currentContent,
        categoryName
      )
    },
    onSuccess: (_, variables) => {
      // Broadcast category deletion via WebSocket
      websocketService.broadcastCategoryDeleted(variables.section, variables.categoryName)

      toast({
        title: 'Sectie verwijderd',
        description: 'De sectie is succesvol verwijderd.',

      })
      onRefetch()
    },
    onError: error => {
      console.error('Error deleting category:', error)
      toast({
        title: 'Fout bij verwijderen',
        description: 'Er is een fout opgetreden bij het verwijderen van de sectie.',
        variant: 'destructive',
      })
    },
  })

  // Handle opening add section dialog
  const handleOpenAddSection = (section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming') => {
    setAddSectionTarget(section)
    setAddSectionDialogOpen(true)
  }

  // Fetch suggestions for inline display
  const { data: suggestions = [] } = useQuery({
    queryKey: ['bob-suggestions', selectedBobAnalyse?.id, activeCrisis?.id],
    queryFn: () => bobAnalyseService.getSuggestions(selectedBobAnalyse!.id),
    enabled: !!selectedBobAnalyse?.id && selectedBobAnalyse?.status === 'completed',
    refetchInterval: (query) => {
      // Poll every 3s while there are pending/processing suggestions
      const data = query.state.data as BobSuggestion[] | undefined
      const hasPending = data?.some(s => s.status === 'pending' || s.status === 'processing')
      return hasPending ? 3000 : false
    },
  })

  // Handle accepting suggestion category - merge into current content
  const handleAcceptSuggestionCategory = useCallback(
    async (
      suggestionId: string,
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
      category: string,
      newItems: string[]
    ) => {
      console.log('[BobAnalyseCard] handleAcceptSuggestionCategory called', {
        suggestionId,
        section,
        category,
        newItemsCount: newItems.length,
        selectedBobAnalyse: !!selectedBobAnalyse
      })

      if (!selectedBobAnalyse) {
        console.error('[BobAnalyseCard] No selectedBobAnalyse')
        return
      }

      try {
        // Get the actual current values from the EditableBox (including unsaved edits)
        const getValuesRefs = section === 'beeldvorming'
          ? beeldvormingGetValuesRefs.current
          : section === 'oordeelsvorming'
          ? oordeelsvormingGetValuesRefs.current
          : besluitvormingGetValuesRefs.current

        console.log('[BobAnalyseCard] getValuesRefs', {
          section,
          category,
          hasRef: !!getValuesRefs[category],
          allCategories: Object.keys(getValuesRefs)
        })

        const getValuesFunction = getValuesRefs[category]
        let currentValues: string[] = []

        if (getValuesFunction) {
          currentValues = getValuesFunction()
          console.log('[BobAnalyseCard] Got values from getValuesFunction')
        } else {
          // Fallback: use values from selectedBobAnalyse state
          const sectionContent = selectedBobAnalyse[section] as Record<string, string[]> | null
          currentValues = sectionContent?.[category] || []
          console.warn('[BobAnalyseCard] getValuesFunction not found, using fallback from selectedBobAnalyse')
        }

        console.log('[BobAnalyseCard] Current values retrieved', {
          currentValuesCount: currentValues.length,
          currentValues: currentValues.slice(0, 3)
        })

        // Merge new items with current values (avoiding duplicates)
        const mergedValues = [...currentValues]
        for (const item of newItems) {
          if (!mergedValues.includes(item)) {
            mergedValues.push(item)
          }
        }

        console.log('[BobAnalyseCard] Merged values', {
          originalCount: currentValues.length,
          newItemsCount: newItems.length,
          mergedCount: mergedValues.length
        })

        // Save merged content (this broadcasts via websocket)
        console.log('[BobAnalyseCard] Calling handleSaveContent')
        await handleSaveContent(section, category, mergedValues)
        console.log('[BobAnalyseCard] handleSaveContent completed')

        // Mark only this specific category as merged in the suggestion
        // Backend will remove this category from the suggestion's content
        // Once all categories are merged, the suggestion will be marked as merged
        const sectionsToMerge = {
          sections: {
            [section]: {
              [category]: newItems,
            },
          },
        }

        console.log('[BobAnalyseCard] Calling mergeSuggestion API')
        await bobAnalyseService.mergeSuggestion(
          selectedBobAnalyse.id,
          suggestionId,
          sectionsToMerge
        )
        console.log('[BobAnalyseCard] mergeSuggestion API completed')

        // Refetch to update UI - backend will have removed this category from the suggestion
        console.log('[BobAnalyseCard] Invalidating queries')
        queryClient.invalidateQueries({ queryKey: ['bob-suggestions', selectedBobAnalyse.id] })
        queryClient.invalidateQueries({ queryKey: ['bob-analyses', activeCrisis?.id] })
        onRefetch()
        console.log('[BobAnalyseCard] handleAcceptSuggestionCategory completed successfully')
      } catch (error) {
        console.error('[BobAnalyseCard] Error in handleAcceptSuggestionCategory:', error)
        toast({
          title: 'Fout bij accepteren suggestie',
          description: error instanceof Error ? error.message : 'Er is een onbekende fout opgetreden',
          variant: 'destructive',
        })
      }
    },
    [selectedBobAnalyse, handleSaveContent, queryClient, activeCrisis?.id, onRefetch, beeldvormingGetValuesRefs, oordeelsvormingGetValuesRefs, besluitvormingGetValuesRefs, toast]
  )

  // Handle rejecting suggestion category - remove only this category
  const handleRejectSuggestionCategory = useCallback(
    async (
      suggestionId: string,
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
      category: string
    ) => {
      if (!selectedBobAnalyse) return

      // Reject only this specific category
      const sectionsToReject = {
        sections: {
          [section]: [category],
        },
      }

      await bobAnalyseService.rejectSuggestion(
        selectedBobAnalyse.id,
        suggestionId,
        sectionsToReject
      )

      // Refetch to update UI - backend will have removed this category from the suggestion
      queryClient.invalidateQueries({ queryKey: ['bob-suggestions', selectedBobAnalyse.id] })
    },
    [selectedBobAnalyse, queryClient]
  )

  // Handle confirming new section
  const handleConfirmAddSection = async (categoryName: string) => {
    if (!addSectionTarget || !selectedBobAnalyse) return

    await addCategoryMutation.mutateAsync({
      section: addSectionTarget,
      categoryName,
    })
  }

  // Handle deleting a section
  const handleDeleteSection = async (
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    categoryName: string
  ) => {
    if (!selectedBobAnalyse) {
      console.error('[BobAnalyseCard] No selected analyse for deletion')
      return
    }

    const currentContent = selectedBobAnalyse[section] as Record<string, string[]> | null
    if (!currentContent) {
      console.error('[BobAnalyseCard] No content found for section:', section)
      return
    }

    console.log('[BobAnalyseCard] Deleting category:', { section, categoryName })

    try {
      await deleteCategoryMutation.mutateAsync({
        section,
        categoryName,
        currentContent,
      })
      console.log('[BobAnalyseCard] Category deleted successfully')
    } catch (error) {
      console.error('[BobAnalyseCard] Failed to delete category:', error)
      throw error // Re-throw so EditableBox can handle it
    }
  }

  // Helper to determine if a category is custom (not in the default set)
  const isCustomCategory = (
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    category: string
  ): boolean => {
    const defaultCategories = {
      beeldvorming: ['oorzaak', 'gebeurtenis', 'gevolg', 'maatregel', 'effect', 'partners'],
      oordeelsvorming: [
        'doelgroepenanalyse',
        'doel(en)',
        'doelen',
        'strategie (ibs)',
        'scenario',
        'scenarios',
        "scenario's",
        'middelen',
        'kernboodschappen',
        'sisos'
      ],
      besluitvorming: ['acties', 'door', 'vergaderklok(ken)', 'vragen?'],
    }

    return !defaultCategories[section].includes(category.toLowerCase())
  }

  const createBobAnalyseMutation = useMutation({
    mutationFn: (scanId: string) => bobAnalyseService.createBobAnalyse({ scanId }),
    onSuccess: async response => {
      // Set local processing state to true
      setIsProcessing(true)

      // Poll for the created BOB analysis without toast notifications
      const pollForResult = async () => {
        try {
          const bobAnalyse = await bobAnalyseService.getBobAnalyse(response.id)
          if (bobAnalyse.status === 'completed' || bobAnalyse.status === 'failed') {
            onRefetch()
            // Keep processing state for a moment to avoid flickering
            setTimeout(() => setIsProcessing(false), 500)
          } else {
            // Continue polling
            setTimeout(pollForResult, 2000)
          }
        } catch (error) {
          console.error('Error polling BOB analysis:', error)
          setTimeout(pollForResult, 2000)
        }
      }

      pollForResult()
    },
    onError: error => {
      console.error('Error creating BOB analysis:', error)
      onRefetch() // Refresh to show error state
      setIsProcessing(false)
    },
  })

  return (
    <>
      <div className="space-y-8">
        {/* Connection status and active users indicator */}
        {/* {selectedBobAnalyse && selectedBobAnalyse.status === 'completed' && !isProcessing && (
          <div className="flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg">

            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-700">Verbonden</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-700">Niet verbonden</span>
                </>
              )}
            </div>


            {Object.keys(activeUsers).length > 0 && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600">
                  {Object.keys(activeUsers).length} gebruiker{Object.keys(activeUsers).length !== 1 ? 's' : ''} actief
                </span>

                <div className="flex -space-x-2">
                  {Object.values(activeUsers)
                    .slice(0, 3)
                    .map((user, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'w-7 h-7 rounded-full bg-blue-100 border-2 border-white',
                          'flex items-center justify-center text-xs font-medium text-blue-700'
                        )}
                        title={user.name}
                      >
                        {user.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    ))}
                  {Object.keys(activeUsers).length > 3 && (
                    <div className="w-7 h-7 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                      +{Object.keys(activeUsers).length - 3}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )} */}

        {/* Sources panels - show when BOB is completed */}
        {selectedBobAnalyse && selectedBobAnalyse.status === 'completed' && !isProcessing && (
          <div className="flex gap-4">
            {/* Add Sources Panel - add scans or custom input */}
            <div className="flex-1">
              <AddSourcesPanel
                bobAnalyseId={selectedBobAnalyse.id}
                onSourceAdded={() => {
                  queryClient.invalidateQueries({ queryKey: ['bob-suggestions', selectedBobAnalyse.id] })
                }}
              />
            </div>

            {/* Source History Panel - show all sources added to this BOB */}
            <div className="w-80">
              <SourceHistoryPanel bobAnalyseId={selectedBobAnalyse.id} />
            </div>
          </div>
        )}

        {/* Processing state - show for draft, processing, or when mutations are pending */}
        {(isProcessing ||
          createBobAnalyseMutation.isPending ||
          (selectedBobAnalyse &&
            (selectedBobAnalyse.status === 'processing' ||
              selectedBobAnalyse.status === 'draft' ||
              selectedBobAnalyse.status === 'queued'))) && (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow border border-blue-100">
            <div className="inline-flex items-center justify-center mb-4 h-12 px-8 rounded-md text-base font-medium border bg-blue-50 border-blue-200 text-blue-700">
              <Loader2 className="mr-3 h-5 w-5 animate-spin" />
              BOB-GOGME analyse wordt gegenereerd...
            </div>
            <span className="text-sm text-muted-foreground">Dit kan enkele minuten duren</span>
          </div>
        )}

        {/* Failed state */}
        {selectedBobAnalyse && selectedBobAnalyse.status === 'failed' && (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow border border-red-100">
            <div className="inline-flex items-center justify-center mb-4 h-12 px-8 rounded-md text-base font-medium border bg-red-50 border-red-200 text-red-700">
              BOB-GOGME analyse generatie mislukt
            </div>
            <span className="text-sm text-muted-foreground">Probeer het opnieuw te genereren</span>
          </div>
        )}

        {/* Show BOB analyse content when selected and status is completed */}
        {selectedBobAnalyse && selectedBobAnalyse.status === 'completed' && !isProcessing && (
          <>
            {/* Beeldvorming - Big standalone box with centered header */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-md">
              <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-orange-500">
                  {t('bob.beeldvorming')}
                </h2>
                {canUpdateBob && !isClosedCrisis && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenAddSection('beeldvorming')}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Voeg sectie toe
                  </Button>
                )}
              </div>
              <div className="p-6">
                {selectedBobAnalyse.beeldvorming ? (
                  <>
                    {(() => {
                      // Map of preferred positions for specific categories
                      const positionMap: Record<string, number> = {
                        oorzaak: 0, // Top left
                        gebeurtenis: 1, // Top center
                        gevolg: 2, // Top right
                        maatregel: 3, // Bottom left
                        effect: 4, // Bottom center
                        partners: 5, // Bottom right
                      }

                      // Extract all categories
                      const beeldvorming = selectedBobAnalyse.beeldvorming as Record<
                        string,
                        string[]
                      >
                      const categories = Object.keys(beeldvorming)

                      // Sort categories by position (defined ones first, then preserve insertion order for custom)
                      const sortedCategories = [...categories].sort((a, b) => {
                        const keyA = a.toLowerCase()
                        const keyB = b.toLowerCase()
                        const posA = keyA in positionMap ? positionMap[keyA] : 999
                        const posB = keyB in positionMap ? positionMap[keyB] : 999
                        if (posA === posB) return categories.indexOf(a) - categories.indexOf(b)
                        return posA - posB
                      })

                      return (
                        <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
                          {sortedCategories.map(category => {
                            const values = beeldvorming[category] || []
                            const isCustom = isCustomCategory('beeldvorming', category)

                            return (
                              <div key={`${selectedBobAnalyse.id}-beeldvorming-${category}-${forceRemount}`}>
                                <EditableBox
                                  title={category}
                                  values={values}
                                  colorScheme="neutral"
                                  section="beeldvorming"
                                  category={category}
                                  registerSave={saveFunction =>
                                    registerBoxSave('beeldvorming', category, saveFunction)
                                  }
                                  registerGetValues={getValuesFunction =>
                                    registerBoxGetValues('beeldvorming', category, getValuesFunction)
                                  }
                                  onSave={newValues =>
                                    handleSaveContent('beeldvorming', category, newValues)
                                  }
                                  isCustom={isCustom}
                                  {...(isCustom && {
                                    onDelete: () => handleDeleteSection('beeldvorming', category),
                                  })}
                                />
                                <CategorySuggestions
                                  section="beeldvorming"
                                  category={category}
                                  suggestions={suggestions}
                                  currentValues={values}
                                  onAcceptCategory={(suggestionId, items) =>
                                    handleAcceptSuggestionCategory(suggestionId, 'beeldvorming', category, items)
                                  }
                                  onRejectCategory={(suggestionId) =>
                                    handleRejectSuggestionCategory(suggestionId, 'beeldvorming', category)
                                  }
                                  disabled={!canUpdateBob || isClosedCrisis}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </>
                ) : (
                  <p className="text-muted-foreground italic text-sm">
                    Geen beeldvorming beschikbaar
                  </p>
                )}
              </div>
            </div>

            {/* Oordeelsvorming - Big standalone box with centered header */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-md">
              <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-orange-500">
                  {t('bob.oordeelsvorming')}
                </h2>
                {canUpdateBob && !isClosedCrisis && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenAddSection('oordeelsvorming')}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Voeg sectie toe
                  </Button>
                )}
              </div>
              <div className="p-6">
                {selectedBobAnalyse.oordeelsvorming ? (
                  <>
                    {(() => {
                      // Map of preferred positions for specific categories
                      const positionMap: Record<string, number> = {
                        'detroffen cirkel': 0,
                        'doel(en)': 1,
                        doelen: 1,
                        'strategie (ibs)': 2,
                        scenario: 3,
                        scenarios: 3,
                        "scenario's": 3,
                        middelen: 4,
                        kernboodschappen: 5,
                      }

                      // Extract all categories
                      const oordeelsvorming = selectedBobAnalyse.oordeelsvorming as Record<
                        string,
                        string[]
                      >
                      const categories = Object.keys(oordeelsvorming)

                      // Sort categories by position (defined ones first, then preserve insertion order for custom)
                      const sortedCategories = [...categories].sort((a, b) => {
                        const keyA = a.toLowerCase()
                        const keyB = b.toLowerCase()
                        const posA = keyA in positionMap ? positionMap[keyA] : 999
                        const posB = keyB in positionMap ? positionMap[keyB] : 999
                        if (posA === posB) return categories.indexOf(a) - categories.indexOf(b)
                        return posA - posB
                      })

                      return (
                        <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
                          {sortedCategories.map(category => {
                            const values = oordeelsvorming[category] || []
                            const isCustom = isCustomCategory('oordeelsvorming', category)

                            return (
                              <div key={`${selectedBobAnalyse.id}-oordeelsvorming-${category}-${forceRemount}`}>
                                <EditableBox
                                  title={category}
                                  values={values}
                                  colorScheme="neutral"
                                  section="oordeelsvorming"
                                  category={category}
                                  registerSave={saveFunction =>
                                    registerBoxSave('oordeelsvorming', category, saveFunction)
                                  }
                                  registerGetValues={getValuesFunction =>
                                    registerBoxGetValues('oordeelsvorming', category, getValuesFunction)
                                  }
                                  onSave={newValues =>
                                    handleSaveContent('oordeelsvorming', category, newValues)
                                  }
                                  isCustom={isCustom}
                                  {...(isCustom && {
                                    onDelete: () => handleDeleteSection('oordeelsvorming', category),
                                  })}
                                />
                                <CategorySuggestions
                                  section="oordeelsvorming"
                                  category={category}
                                  suggestions={suggestions}
                                  currentValues={values}
                                  onAcceptCategory={(suggestionId, items) =>
                                    handleAcceptSuggestionCategory(suggestionId, 'oordeelsvorming', category, items)
                                  }
                                  onRejectCategory={(suggestionId) =>
                                    handleRejectSuggestionCategory(suggestionId, 'oordeelsvorming', category)
                                  }
                                  disabled={!canUpdateBob || isClosedCrisis}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </>
                ) : (
                  <p className="text-muted-foreground italic text-sm">
                    Geen oordeelsvorming beschikbaar
                  </p>
                )}
              </div>
            </div>

            {/* Besluitvorming - Big standalone box with centered header */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-md">
              <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-orange-500">Besluitvorming</h2>
                {canUpdateBob && !isClosedCrisis && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenAddSection('besluitvorming')}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Voeg sectie toe
                  </Button>
                )}
              </div>
              <div className="p-6">
                {selectedBobAnalyse.besluitvorming ? (
                  <>
                    {(() => {
                      const besluitvorming = selectedBobAnalyse.besluitvorming as Record<
                        string,
                        string[]
                      >
                      const categories = Object.keys(besluitvorming)

                      // Sort categories: Acties, Door, then others in insertion order
                      const sortedCategories = [...categories].sort((a, b) => {
                        const order = ['acties', 'door', 'vergaderklok(ken)', 'vragen?']
                        const aLower = a.toLowerCase()
                        const bLower = b.toLowerCase()
                        const aIndex = order.indexOf(aLower)
                        const bIndex = order.indexOf(bLower)

                        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
                        if (aIndex !== -1) return -1
                        if (bIndex !== -1) return 1
                        return categories.indexOf(a) - categories.indexOf(b)
                      })

                      return (
                        <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
                          {sortedCategories.map(category => {
                            const values = besluitvorming[category] || []
                            const isCustom = isCustomCategory('besluitvorming', category)

                            return (
                              <div key={`${selectedBobAnalyse.id}-besluitvorming-${category}-${forceRemount}`}>
                                <EditableBox
                                  title={category}
                                  values={values}
                                  colorScheme="neutral"
                                  section="besluitvorming"
                                  category={category}
                                  registerSave={saveFunction =>
                                    registerBoxSave('besluitvorming', category, saveFunction)
                                  }
                                  registerGetValues={getValuesFunction =>
                                    registerBoxGetValues('besluitvorming', category, getValuesFunction)
                                  }
                                  onSave={newValues =>
                                    handleSaveContent('besluitvorming', category, newValues)
                                  }
                                  isCustom={isCustom}
                                  {...(isCustom && {
                                    onDelete: () => handleDeleteSection('besluitvorming', category),
                                  })}
                                />
                                <CategorySuggestions
                                  section="besluitvorming"
                                  category={category}
                                  suggestions={suggestions}
                                  currentValues={values}
                                  onAcceptCategory={(suggestionId, items) =>
                                    handleAcceptSuggestionCategory(suggestionId, 'besluitvorming', category, items)
                                  }
                                  onRejectCategory={(suggestionId) =>
                                    handleRejectSuggestionCategory(suggestionId, 'besluitvorming', category)
                                  }
                                  disabled={!canUpdateBob || isClosedCrisis}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </>
                ) : (
                  <p className="text-muted-foreground italic text-sm">
                    Geen besluitvorming beschikbaar
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Section Dialog */}
      <AddSectionDialog
        open={addSectionDialogOpen}
        onOpenChange={setAddSectionDialogOpen}
        onConfirm={handleConfirmAddSection}
        sectionName={
          addSectionTarget === 'beeldvorming'
            ? 'Beeldvorming'
            : addSectionTarget === 'oordeelsvorming'
              ? 'Oordeelsvorming'
              : 'Besluitvorming'
        }
        existingCategories={
          selectedBobAnalyse && addSectionTarget
            ? Object.keys(selectedBobAnalyse[addSectionTarget] as Record<string, string[]> || {})
            : []
        }
      />
    </>
  )
}
